import { MoneyDisplay } from '@/components/ui/MoneyDisplay';

interface PnlChartTooltipPayload {
    periodLabel: string;
    netProfit: number;
    gainPercentage: number | null;
    changeFromPrevious: number | null;
    isCarriedForward: boolean;
}

interface PnlChartTooltipProps {
    /** Whether the tooltip is active (from Recharts) */
    active?: boolean;
    /** Payload data from Recharts */
    payload?: ReadonlyArray<{ payload?: PnlChartTooltipPayload }>;
    /** Display currency for the values */
    currency: string;
}

/**
 * Format a percentage value for display.
 */
function formatPercentage(value: number | null | undefined): string {
    if (value === null || value === undefined) {
        return 'N/A';
    }
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
}

/**
 * Custom tooltip component for the P&L chart.
 * Displays the period label, net profit, gain percentage, and change from previous period.
 *
 * For P&L:
 * - Positive net profit = green (gain)
 * - Negative net profit = red (loss)
 *
 * This component is used as the `content` prop of Recharts' Tooltip component.
 */
export function PnlChartTooltip({ active, payload, currency }: PnlChartTooltipProps) {
    if (!active || !payload || payload.length === 0) {
        return null;
    }

    const data = payload[0]?.payload;
    if (!data) {
        return null;
    }

    const { periodLabel, netProfit, gainPercentage, isCarriedForward } = data;

    // Determine color for net profit
    const profitColorClass = netProfit > 0
        ? 'text-green-600 dark:text-green-400'
        : netProfit < 0
            ? 'text-red-600 dark:text-red-400'
            : 'text-gray-500 dark:text-gray-400';

    return (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary shadow-lg p-2 sm:p-3 sm:min-w-[180px]">
            {/* Period label */}
            <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1">
                {periodLabel}
            </p>

            {/* Desktop: labeled rows */}
            <div className="hidden sm:block">
                <div className="flex items-center justify-between gap-3 mb-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Net Profit:</span>
                    <span className={`font-semibold ${profitColorClass}`}>
                        <MoneyDisplay
                            value={netProfit}
                            currency={currency}
                            size="md"
                            showSign={true}
                            className="inline"
                        />
                    </span>
                </div>
                {gainPercentage !== null && (
                    <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex justify-between items-center gap-3 text-xs">
                            <span className="text-gray-500 dark:text-gray-400">Gain %:</span>
                            <span className={`font-medium ${profitColorClass}`}>
                                {formatPercentage(gainPercentage)}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile: vertical stacked layout with labels */}
            <div className="sm:hidden space-y-1">
                <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Net Profit</div>
                    <span className={`font-semibold ${profitColorClass}`}>
                        <MoneyDisplay
                            value={netProfit}
                            currency={currency}
                            size="sm"
                            showSign={true}
                            className="inline"
                        />
                    </span>
                </div>
                {gainPercentage !== null && (
                    <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Gain %</div>
                        <div className={`text-xs font-medium ${profitColorClass}`}>
                            {formatPercentage(gainPercentage)}
                        </div>
                    </div>
                )}
            </div>

            {isCarriedForward && (
                <p className="hidden sm:block text-xs text-gray-500 dark:text-gray-400 mt-1 sm:mt-2 italic">
                    Carried forward
                </p>
            )}
        </div>
    );
}
